import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { Permissao } from '../types/permissao.type';
import PermissaoGenerica from '../types/permissaoGenerica';
import { Cargo } from '../types/cargo.types';


interface UserPermission {
    id: number
    acao?: string;
    descricao?: string;
    entidade?: string;
}
interface User {
  id: number;
  nome: string;
  email: string;
  isAdmin:boolean;
  permissoes: UserPermission[];
  cargos?:Cargo[]
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean; // Adiciona o estado de carregamento
  login: (user: User) => void;
  logout: () => void;
  hasPermission: ( requiredPermission: PermissaoGenerica ) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Inicia como true para indicar que a verificação está em andamento

  useEffect(() => {
    try {
      const token = localStorage.getItem('@GM:token');
      const userData = localStorage.getItem('@GM:user');
      
      if (token && userData) {
        setIsAuthenticated(true);
        setUser(JSON.parse(userData));
      }
    } finally {
      setLoading(false); // Finaliza o carregamento, independentemente do resultado
    }
  }, []);

  const login = (userData: User) => {
    setIsAuthenticated(true);
    setUser(userData);
    localStorage.setItem('@GM:user', JSON.stringify(userData));
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('@GM:user');
    authService.logout();
  };

  const hasPermission = ( requiredPermission: PermissaoGenerica ): boolean => {
    if (!user) return false;
    if (user.isAdmin) return true;

    const temPermissaoDireta = user.permissoes.some(p => p.acao === requiredPermission.acao && p.entidade === requiredPermission.entidade);
    const temPermissaoPorCargo = user.cargos?.some(cargo => cargo.permissoes.some(p => p.acao === requiredPermission.acao && p.entidade === requiredPermission.entidade));

    return temPermissaoDireta || !!temPermissaoPorCargo;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, loading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuthContext deve ser usado dentro de AuthProvider');
  return context;
} 